import { useState, useEffect, useRef, useCallback } from 'react';
import axios_instance from '../api/axios_instance';
import { Loader2, Eye, MapPin, User, Search, Inbox, Package, ChevronUp, Clock } from 'lucide-react';

const ManageOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  
  // States for Infinite Scroll & Filters
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  const observer = useRef();

  // ملاحقة العنصر الأخير لتفعيل التحميل التلقائي
  const lastOrderElementRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore]);

  // جلب البيانات عند تغيير الصفحة أو الفلتر
  useEffect(() => {
    const fetchInitial = async () => {
      setLoading(true);
      try {
        const res = await axios_instance.get(`/api/orders?page=1&status=${statusFilter}&search=${searchTerm}`);
        setOrders(res.data.orders);
        setHasMore(res.data.orders.length < res.data.totalOrders);
        setPage(1);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, [statusFilter, searchTerm]);

  // جلب المزيد عند التمرير
  useEffect(() => {
    if (page === 1) return;
    const fetchMore = async () => {
      setLoadingMore(true);
      try {
        const res = await axios_instance.get(`/api/orders?page=${page}&status=${statusFilter}&search=${searchTerm}`);
        setOrders(prev => [...prev, ...res.data.orders]);
        setHasMore(orders.length + res.data.orders.length < res.data.totalOrders);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingMore(false);
      }
    };
    fetchMore();
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, newStatus) => {
    try {
      const res = await axios_instance.patch(`/api/orders/${id}/status`, { status: newStatus });
      if (res.status === 200) {
        setOrders(prev => prev.map(o => o._id === id ? { ...o, status: newStatus } : o));
      }
    } catch (err) {
      alert("فشل التحديث");
    }
  };

  const STATUS_AR = {
    'Pending':   'قيد الانتظار',
    'Confirmed': 'تم التأكيد',
    'Delivered': 'تم التسليم',
    'Cancelled': 'ملغي',
  };

  const getStatusStyle = (status) => {
    const styles = {
      'Pending':   'border-amber-500 text-amber-600 bg-amber-50',
      'Confirmed': 'border-blue-500 text-blue-600 bg-blue-50',
      'Delivered': 'border-emerald-500 text-emerald-600 bg-emerald-50',
      'Cancelled': 'border-red-500 text-red-600 bg-red-50',
    };
    return styles[status] || 'border-gray-400 text-gray-500 bg-gray-50';
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" dir="rtl">
      {/* Header & Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h2 className="text-4xl font-black text-gray-900">الطلبيات</h2>
          <p className="text-gray-500 font-bold mt-2">إدارة ومتابعة طلبات العملاء المباشرة</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="relative group">
            <Search className="absolute right-3 top-3 text-gray-400" size={20} />
            <input 
              type="text" 
              placeholder="بحث بالاسم أو رقم الهاتف..." 
              className="bg-white border-2 border-gray-200 rounded-2xl px-10 py-3 text-sm font-bold outline-none focus:border-emerald-500 transition-all w-full md:w-72"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white border-2 border-gray-200 rounded-2xl px-6 py-3 text-sm font-black outline-none focus:border-emerald-500 cursor-pointer"
          >
            <option value="All">جميع الحالات</option>
            <option value="Pending">قيد الانتظار</option>
            <option value="Confirmed">تم التأكيد</option>
            <option value="Delivered">تم التسليم</option>
            <option value="Cancelled">ملغي</option>
          </select>
        </div>
      </div>

      {/* Orders List */}
      {loading && page === 1 ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-emerald-600" size={48} /></div>
      ) : orders.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-[40px] p-20 text-center space-y-4">
          <Inbox size={64} className="mx-auto text-gray-300" />
          <p className="text-xl font-black text-gray-400">لا توجد طلبيات مطابقة للبحث</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {orders.map((order, index) => {
            const isLast = orders.length === index + 1;
            return (
              <div 
                key={order._id} 
                ref={isLast ? lastOrderElementRef : null}
                className={`bg-white border-2 transition-all overflow-hidden rounded-[30px] ${expandedOrderId === order._id ? 'border-emerald-500' : 'border-gray-100'}`}
              >
                {/* Order Summary Header */}
                <div className="p-6 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-6">
                    <div className="bg-gray-100 p-4 rounded-2xl">
                      <Package className="text-gray-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-black text-xl text-gray-900">{order.orderNumber}</h3>
                      <div className="flex items-center gap-2 text-gray-500 text-sm font-bold">
                        <Clock size={14} /> <span>{new Date(order.createdAt).toLocaleDateString('ar-TN')}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-10">
                    <div className="hidden md:block">
                      <p className="text-[10px] font-black text-gray-400 uppercase mb-1">العميل</p>
                      <p className="font-bold text-gray-800">{order.customer.firstName} {order.customer.lastName}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase mb-1">المبلغ الإجمالي</p>
                      <p className="font-black text-emerald-600 text-lg">{order.totalPrice.toFixed(3)} DT</p>
                    </div>
                    <span className={`px-4 py-1.5 rounded-full text-xs font-black border-2 ${getStatusStyle(order.status)}`}>
                      {STATUS_AR[order.status] || order.status}
                    </span>
                    <button 
                      onClick={() => setExpandedOrderId(expandedOrderId === order._id ? null : order._id)}
                      className={`p-3 rounded-xl transition-colors ${expandedOrderId === order._id ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                    >
                      {expandedOrderId === order._id ? <ChevronUp size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedOrderId === order._id && (
                  <div className="p-8 bg-gray-50/50 border-t-2 border-gray-100 animate-in slide-in-from-top-4 duration-300">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      {/* Customer & Shipping */}
                      <div className="space-y-4">
                        <h4 className="font-black text-gray-900 flex items-center gap-2 italic">01. تفاصيل الشحن</h4>
                        <div className="bg-white p-6 border-2 border-gray-100 rounded-[25px] space-y-4">
                          <div className="flex items-start gap-3">
                            <User className="text-emerald-500 mt-1" size={18} />
                            <div>
                                <p className="text-sm font-black">{order.customer.firstName} {order.customer.lastName}</p>
                                <p className="text-xs text-gray-500 font-bold" dir="ltr">{order.customer.phone}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <MapPin className="text-red-500 mt-1" size={18} />
                            <p className="text-sm font-bold text-gray-600 leading-relaxed">
                              {order.customer.city}, {order.customer.address}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Items */}
                      <div className="lg:col-span-2 space-y-4">
                        <h4 className="font-black text-gray-900 flex items-center gap-2 italic">02. السلة</h4>
                        <div className="bg-white border-2 border-gray-100 rounded-[25px] overflow-hidden">
                          <div className="p-4 bg-gray-50 border-b-2 border-gray-100 grid grid-cols-3 text-[10px] font-black text-gray-400 uppercase">
                            <span>المنتج</span>
                            <span className="text-center">الكمية</span>
                            <span className="text-left">السعر</span>
                          </div>
                          {order.items.map((item, idx) => (
                            <div key={idx} className="p-4 grid grid-cols-3 items-center border-b border-gray-50 last:border-none">
                              <span className="text-sm font-black text-gray-800">{item.name?.ar || item.name?.en}</span>
                              <span className="text-center font-bold text-gray-500">{item.quantity}</span>
                              <span className="text-left font-black text-gray-900">{(item.price * item.quantity).toFixed(3)} DT</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Actions Area */}
                    <div className="mt-8 pt-6 border-t-2 border-gray-100 flex flex-wrap gap-3">
                      <button onClick={() => updateStatus(order._id, 'Confirmed')} disabled={order.status === 'Confirmed'} className="flex-1 min-w-[140px] py-4 bg-gray-900 text-white rounded-2xl font-black text-sm hover:bg-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed">تأكيد الطلب</button>
                      <button onClick={() => updateStatus(order._id, 'Delivered')} disabled={order.status === 'Delivered'} className="flex-1 min-w-[140px] py-4 bg-emerald-600 text-white rounded-2xl font-black text-sm hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">تم التسليم</button>
                      <button onClick={() => updateStatus(order._id, 'Cancelled')} disabled={order.status === 'Cancelled'} className="flex-1 min-w-[140px] py-4 border-2 border-red-200 text-red-500 rounded-2xl font-black text-sm hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">إلغاء الطلب</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Loading More Indicator */}
      {loadingMore && (
        <div className="flex justify-center py-10">
          <Loader2 className="animate-spin text-emerald-600" size={32} />
        </div>
      )}

      {!hasMore && orders.length > 0 && (
        <p className="text-center text-gray-400 font-bold py-10">لقد وصلت إلى نهاية القائمة ✨</p>
      )}
    </div>
  );
};

export default ManageOrders;